import { useEffect, useState } from 'react';
import { api, type PlatformClinicDetail, type PlatformOverview, type SupportGrant } from '../api';
import { useI18n } from '../i18n';
import { EmptyState, ErrorState, Field, Modal, Skeleton, Spinner, Tabs } from '../components';
import { IconPlus, IconSearch } from '../icons';
import { describe } from '../errors';

/**
 * The platform console — the vendor's side of the product (spec §5, §21).
 *
 * This page answers the question "who runs all of this". Exactly one kind of
 * account: a platform operator. It is not a senior clinic role — a clinic owner
 * owns their own clinic and nothing beyond it, and the permission editor inside
 * a clinic refuses to grant a `platform.*` permission at all, so there is no
 * path from one to the other.
 *
 * What an operator can do is narrow on purpose: create a clinic and its first
 * owner, set what that clinic has paid for, issue activation codes, suspend a
 * clinic for non-payment, and enter one briefly to help. What they cannot do is
 * read patient records at will — support access is time-boxed, needs a written
 * reason, and is written into the CLINIC's own audit log, where the customer
 * sees it without being told.
 */
export function Platform() {
  const { t } = useI18n();
  const [tab, setTab] = useState('clinics');

  return (
    <>
      <div className="page-head">
        <div>
          <h1>{t.platform.title}</h1>
          <div className="page-sub">{t.platform.subtitle}</div>
        </div>
      </div>

      <Tabs
        value={tab}
        onChange={setTab}
        tabs={[
          { value: 'clinics', label: t.platform.clinics },
          { value: 'support', label: t.platform.support },
          { value: 'operators', label: t.platform.operators },
        ]}
      />

      {tab === 'clinics' ? <Clinics /> : null}
      {tab === 'support' ? <Support /> : null}
      {tab === 'operators' ? <Operators /> : null}
    </>
  );
}

function Clinics() {
  const { t, locale, formatDate, formatNumber } = useI18n();
  const [data, setData] = useState<PlatformOverview | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [q, setQ] = useState('');
  const [debounced, setDebounced] = useState('');
  const [creating, setCreating] = useState(false);
  const [importing, setImporting] = useState(false);
  const [openId, setOpenId] = useState<string | null>(null);
  const [nonce, setNonce] = useState(0);

  useEffect(() => {
    const id = setTimeout(() => setDebounced(q), 250);
    return () => clearTimeout(id);
  }, [q]);

  useEffect(() => {
    setData(null);
    api
      .platformOverview(debounced || undefined)
      .then(setData)
      .catch((e) => setError(describe(e, t)));
  }, [debounced, nonce, t]);

  if (error) return <ErrorState message={error} onRetry={() => setNonce((n) => n + 1)} />;

  const name = (c: { nameEn: string; nameAr: string }) => (locale === 'ar' ? c.nameAr : c.nameEn);

  return (
    <>
      <div className="notice" style={{ marginBottom: 18 }}>
        <div>
          <strong>{t.platform.ownerQuestion}</strong>
          <div style={{ marginTop: 4 }}>{t.platform.ownerAnswer}</div>
        </div>
      </div>

      {data ? (
        <div className="stats">
          <Stat label={t.platform.clinics} value={formatNumber(data.totals.clinics)} tone="teal" />
          <Stat label={t.platform.active} value={formatNumber(data.totals.active)} tone="blue" />
          <Stat label={t.platform.expiringSoon} value={formatNumber(data.totals.expiringSoon)} tone="amber" />
          <Stat label={t.platform.expired} value={formatNumber(data.totals.expired)} tone="amber" />
          <Stat label={t.platform.paused} value={formatNumber(data.totals.paused)} tone="violet" />
        </div>
      ) : null}

      {/* Who needs a call this week: lapsed, about to lapse, or on hold. */}
      {data ? (
        <div className="card" style={{ marginBottom: 18 }}>
          <div className="card-head"><h2>{t.platform.needsAttention}</h2></div>
          <div className="card-body">
            {data.attention.length === 0 ? (
              <span style={{ color: 'var(--ink-3)' }}>{t.platform.nothingNeedsAttention}</span>
            ) : (
              <ul style={{ margin: 0, paddingInlineStart: 18 }}>
                {data.attention.map((a) => (
                  <li key={a.id} style={{ marginBottom: 6 }}>
                    <button type="button" className="btn-link" onClick={() => setOpenId(a.id)}>
                      <strong>{name(a)}</strong>
                    </button>
                    {a.ownerEmail ? <span className="sub ltr-value" style={{ display: 'inline', marginInlineStart: 8 }}>{a.ownerEmail}</span> : null}{' '}
                    {a.paused ? (
                      <span className="badge warn">{t.platform.pausedUntil} {a.pausedUntil ? formatDate(a.pausedUntil) : ''}</span>
                    ) : a.expired ? (
                      <span className="badge danger">{t.platform.expired}{a.endsAt ? ` · ${formatDate(a.endsAt)}` : ''}</span>
                    ) : (
                      <span className="badge warn">{t.platform.daysLeft.replace('{days}', String(a.daysLeft ?? 0))}</span>
                    )}
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>
      ) : null}

      <div className="toolbar">
        <div className="search-box">
          <IconSearch size={16} className="icon" />
          <input value={q} onChange={(e) => setQ(e.target.value)} placeholder={t.platform.clinics} />
        </div>
        <div className="toolbar-spacer" />
        <button type="button" className="btn btn-ghost" onClick={() => setImporting(true)}>
          {t.platform.importClinic}
        </button>
        <button type="button" className="btn" onClick={() => setCreating(true)}>
          <IconPlus size={16} />
          {t.platform.newClinic}
        </button>
      </div>

      <div className="card">
        {!data ? (
          <Skeleton rows={5} />
        ) : data.clinics.length === 0 ? (
          <EmptyState title={t.platform.noClinics} />
        ) : (
          <div className="table-scroll">
            <table>
              <thead>
                <tr>
                  <th>{t.platform.clinicName}</th>
                  <th>{t.platform.owner}</th>
                  <th>{t.platform.plan}</th>
                  <th>{t.platform.code}</th>
                  <th>{t.platform.starts}</th>
                  <th>{t.platform.ends}</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {data.clinics.map((c) => (
                  <tr key={c.id}>
                    <td>
                      <button type="button" className="btn-link" onClick={() => setOpenId(c.id)}>
                        <strong>{name(c)}</strong>
                      </button>
                      <span className="sub ltr-value">/c/{c.slug}</span>
                    </td>
                    <td className="ltr-value">{c.ownerEmail ?? '—'}</td>
                    <td>
                      <span className="badge brand">{c.plan ?? '—'}</span>
                      {c.status !== 'active' ? (
                        <span className="badge warn" style={{ marginInlineStart: 6 }}>{c.status}</span>
                      ) : null}
                      {c.paused ? (
                        <span className="badge warn" style={{ marginInlineStart: 6 }}>{t.platform.paused}</span>
                      ) : null}
                      {c.expired ? (
                        <span className="badge danger" style={{ marginInlineStart: 6 }}>{t.platform.expired}</span>
                      ) : c.expiringSoon ? (
                        <span className="badge warn" style={{ marginInlineStart: 6 }}>{t.platform.daysLeft.replace('{days}', String(c.daysLeft ?? 0))}</span>
                      ) : null}
                      <span className="sub">
                        {formatNumber(c.activeMembers)} {t.platform.seats.toLowerCase()} · {formatNumber(c.dentists)} {t.platform.dentists.toLowerCase()}
                      </span>
                    </td>
                    <td>
                      {/* The clinic's licence code, readable: what the operator reads out again. */}
                      {c.activationCode ? <code className="ltr-value" style={{ userSelect: 'all' }}>{c.activationCode}</code> : <span style={{ color: 'var(--ink-3)' }}>{t.platform.noCode}</span>}
                    </td>
                    <td>{c.startsAt ? formatDate(c.startsAt) : '—'}</td>
                    <td>{c.endsAt ? formatDate(c.endsAt) : '—'}</td>
                    <td style={{ textAlign: 'end', whiteSpace: 'nowrap' }}>
                      <button type="button" className="btn-link" onClick={() => setOpenId(c.id)}>
                        {t.platform.details}
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {creating ? (
        <NewClinic
          plans={Object.keys(data?.plans ?? {})}
          onClose={() => setCreating(false)}
          onDone={() => {
            setCreating(false);
            setNonce((n) => n + 1);
          }}
        />
      ) : null}

      {importing ? (
        <ImportClinic
          onClose={() => setImporting(false)}
          onDone={() => {
            setImporting(false);
            setNonce((n) => n + 1);
          }}
        />
      ) : null}

      {openId ? (
        <ClinicDetail
          clinicId={openId}
          plans={Object.keys(data?.plans ?? {})}
          onClose={() => setOpenId(null)}
          onChanged={() => setNonce((n) => n + 1)}
        />
      ) : null}
    </>
  );
}

/** The clinic's code, readable, with a copy button. */
function CodeReveal({ code, email, note }: { code: string; email?: string; note: string }) {
  const { t } = useI18n();
  const [copied, setCopied] = useState(false);
  return (
    <>
      <div className="notice">
        <span>{note}</span>
      </div>
      <div
        className="ltr-value"
        style={{
          fontSize: 20,
          fontWeight: 600,
          letterSpacing: '0.06em',
          background: 'var(--brand-50)',
          border: '1px solid var(--brand-100)',
          borderRadius: 'var(--radius-sm)',
          padding: '18px 16px',
          textAlign: 'center',
          userSelect: 'all',
        }}
      >
        {code}
      </div>
      {email ? <div className="sub ltr-value" style={{ textAlign: 'center' }}>{email}</div> : null}
      <button type="button" className="btn btn-ghost" onClick={() => void navigator.clipboard?.writeText(code).then(() => setCopied(true))}>
        {copied ? t.platform.copied : t.platform.copy}
      </button>
    </>
  );
}

/**
 * One clinic, in full: who is in it, its code, its dates — and the three
 * things the operator does to a clinic: hold it, export it, remove it.
 */
function ClinicDetail({
  clinicId,
  plans,
  onClose,
  onChanged,
}: {
  clinicId: string;
  plans: string[];
  onClose: () => void;
  onChanged: () => void;
}) {
  const { t, locale, formatDate, formatNumber } = useI18n();
  const [data, setData] = useState<PlatformClinicDetail | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [nonce, setNonce] = useState(0);
  const [busy, setBusy] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [dates, setDates] = useState({ plan: '', startsOn: '', endsOn: '', months: '12' });
  const [pause, setPause] = useState({ days: '7', reason: '' });
  const [confirm, setConfirm] = useState({ slug: '', reason: '' });
  const [status, setStatus] = useState({ reason: '' });

  useEffect(() => {
    api
      .platformClinic(clinicId)
      .then((d) => {
        setData(d);
        setDates((f) => ({ ...f, plan: d.subscription?.plan ?? plans[0] ?? 'solo' }));
      })
      .catch((e) => setError(describe(e, t)));
  }, [clinicId, nonce, t, plans]);

  const changed = () => {
    setNonce((n) => n + 1);
    onChanged();
  };
  const act = async (key: string, fn: () => Promise<void>) => {
    setBusy(key);
    setError(null);
    setNotice(null);
    try {
      await fn();
      changed();
    } catch (e) {
      setError(describe(e, t));
    } finally {
      setBusy(null);
    }
  };

  if (error && !data) return <Modal title={t.platform.details} onClose={onClose}><ErrorState message={error} /></Modal>;
  if (!data) return <Modal title={t.platform.details} onClose={onClose}><Skeleton rows={6} /></Modal>;

  const c = data.clinic;
  const name = locale === 'ar' ? c.nameAr : c.nameEn;
  const suspended = c.status !== 'active';

  return (
    <Modal title={name} onClose={onClose}>
      <div className="stack">
        {error ? <div className="notice danger" role="alert">{error}</div> : null}
        {notice ? <div className="notice" role="status">{notice}</div> : null}

        <div className="grid-2">
          <div>
            <div className="section-label">{t.platform.owner}</div>
            <div className="ltr-value">{data.activationCode?.email ?? c.email ?? '—'}</div>
          </div>
          <div>
            <div className="section-label">{t.platform.plan}</div>
            <span className="badge brand">{data.subscription?.plan ?? '—'}</span>
            {suspended ? <span className="badge warn" style={{ marginInlineStart: 6 }}>{c.status}</span> : null}
            {c.paused ? <span className="badge warn" style={{ marginInlineStart: 6 }}>{t.platform.pausedUntil} {c.pausedUntil ? formatDate(c.pausedUntil) : ''}</span> : null}
          </div>
          <div>
            <div className="section-label">{t.platform.starts}</div>
            <div>{data.subscription?.startsAt ? formatDate(data.subscription.startsAt) : '—'}</div>
          </div>
          <div>
            <div className="section-label">{t.platform.ends}</div>
            <div>{data.subscription?.endsAt ? formatDate(data.subscription.endsAt) : '—'}</div>
          </div>
          <div>
            <div className="section-label">{t.platform.patients}</div>
            <div>{formatNumber(c.patients)}</div>
          </div>
          <div>
            <div className="section-label">{t.platform.address}</div>
            <div className="ltr-value">/c/{c.slug}</div>
          </div>
          <div>
            <div className="section-label">{t.platform.policy}</div>
            <div>
              {c.policyAcceptedAt ? (
                `${t.platform.policyAccepted} ${formatDate(c.policyAcceptedAt)}`
              ) : (
                <span className="badge warn">{t.platform.policyNotAccepted}</span>
              )}
            </div>
          </div>
        </div>

        {/* The code: what the operator reads out again. */}
        <div>
          <div className="section-label">{t.platform.code}</div>
          {data.activationCode?.code ? (
            <CodeReveal code={data.activationCode.code} email={data.activationCode.email} note={t.platform.licenceNote} />
          ) : (
            <span style={{ color: 'var(--ink-3)' }}>{t.platform.noCode}</span>
          )}
          <button
            type="button"
            className="btn btn-ghost btn-sm"
            disabled={busy !== null}
            onClick={() =>
              void act('code', async () => {
                const r = await api.reissueOwnerCode(clinicId);
                setNotice((r.emailed ? t.platform.codeEmailed : t.platform.codeNotEmailed).replace('{email}', r.email));
              })
            }
          >
            {busy === 'code' ? <Spinner /> : t.platform.newCode}
          </button>
          {data.activationCode?.code ? (
            <button
              type="button"
              className="btn btn-ghost btn-sm"
              style={{ marginInlineStart: 8 }}
              disabled={busy !== null}
              onClick={() =>
                void act('email', async () => {
                  const r = await api.emailOwnerCode(clinicId);
                  setNotice(t.platform.codeEmailed.replace('{email}', r.email));
                })
              }
            >
              {busy === 'email' ? <Spinner /> : t.platform.emailCode}
            </button>
          ) : null}
        </div>

        {/* Who works here. Names and emails, nothing clinical. */}
        <div>
          <div className="section-label">{t.platform.users}</div>
          {data.members.length === 0 ? (
            <span style={{ color: 'var(--ink-3)' }}>{t.platform.noUsers}</span>
          ) : (
            <div className="table-scroll">
              <table>
                <tbody>
                  {data.members.map((m) => (
                    <tr key={m.membershipId}>
                      <td style={{ paddingInlineStart: 0 }}>
                        <strong>{m.name}</strong>
                        <span className="sub ltr-value">{m.email}</span>
                      </td>
                      <td>
                        {m.roles.map((r) => (
                          <span key={r} className="badge" style={{ marginInlineEnd: 4 }}>{r.replace(/_/g, ' ')}</span>
                        ))}
                        {m.isDentist ? <span className="badge brand">{t.platform.dentists}</span> : null}
                      </td>
                      <td style={{ paddingInlineEnd: 0, textAlign: 'end' }}>
                        {m.status === 'active' ? (
                          <span className="badge ok">{t.platform.activatedOn}{m.activatedAt ? ` ${formatDate(m.activatedAt)}` : ''}</span>
                        ) : (
                          <span className="badge warn">{m.status === 'invited' ? t.platform.notActivated : m.status}</span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* Dates: the first and last day, or months added. */}
        <div className="card">
          <div className="card-head"><h2>{t.platform.dates}</h2></div>
          <div className="card-body stack">
            <div className="notice"><span>{t.platform.datesHint}</span></div>
            <div className="grid-2">
              <Field label={t.platform.plan} required>
                <select value={dates.plan} onChange={(e) => setDates({ ...dates, plan: e.target.value })}>
                  {plans.map((p) => (
                    <option key={p} value={p}>{p}</option>
                  ))}
                </select>
              </Field>
              <Field label={t.platform.months}>
                <input value={dates.months} onChange={(e) => setDates({ ...dates, months: e.target.value })} inputMode="numeric" className="ltr-value" disabled={!!dates.endsOn} />
              </Field>
              <Field label={t.platform.startsOn}>
                <input type="date" value={dates.startsOn} onChange={(e) => setDates({ ...dates, startsOn: e.target.value })} className="ltr-value" />
              </Field>
              <Field label={t.platform.endsOn}>
                <input type="date" value={dates.endsOn} onChange={(e) => setDates({ ...dates, endsOn: e.target.value })} className="ltr-value" />
              </Field>
            </div>
            <div className="row" style={{ justifyContent: 'flex-end' }}>
              <button
                type="button"
                className="btn"
                disabled={busy !== null || !dates.plan || (!dates.endsOn && !(Number(dates.months) > 0))}
                onClick={() =>
                  void act('dates', async () => {
                    await api.setSubscription(clinicId, {
                      plan: dates.plan,
                      months: Number(dates.months) || 12,
                      extend: true,
                      ...(dates.startsOn ? { startsOn: dates.startsOn } : {}),
                      ...(dates.endsOn ? { endsOn: dates.endsOn } : {}),
                    });
                  })
                }
              >
                {busy === 'dates' ? <Spinner /> : t.platform.setDates}
              </button>
            </div>
          </div>
        </div>

        {/* Pause: a hold for payment. Nothing lost, lifts by itself. */}
        <div className="card">
          <div className="card-head"><h2>{t.platform.pause}</h2></div>
          <div className="card-body stack">
            <div className="notice"><span>{t.platform.pauseNote}</span></div>
            {c.paused ? (
              <div className="row" style={{ justifyContent: 'space-between' }}>
                <span className="badge warn">{t.platform.pausedUntil} {c.pausedUntil ? formatDate(c.pausedUntil) : ''}{c.pauseReason ? ` · ${c.pauseReason}` : ''}</span>
                <button type="button" className="btn" disabled={busy !== null} onClick={() => void act('resume', async () => { await api.pauseClinic(clinicId, 0); })}>
                  {busy === 'resume' ? <Spinner /> : t.platform.resume}
                </button>
              </div>
            ) : (
              <>
                <div className="grid-2">
                  <Field label={t.platform.pauseDays} required>
                    <input value={pause.days} onChange={(e) => setPause({ ...pause, days: e.target.value })} inputMode="numeric" className="ltr-value" />
                  </Field>
                  <Field label={t.platform.pauseReason}>
                    <input value={pause.reason} onChange={(e) => setPause({ ...pause, reason: e.target.value })} />
                  </Field>
                </div>
                <div className="row" style={{ justifyContent: 'flex-end' }}>
                  <button
                    type="button"
                    className="btn btn-danger"
                    disabled={busy !== null || !(Number(pause.days) > 0)}
                    onClick={() => void act('pause', async () => { await api.pauseClinic(clinicId, Number(pause.days), pause.reason || undefined); })}
                  >
                    {busy === 'pause' ? <Spinner /> : t.platform.pause}
                  </button>
                </div>
              </>
            )}
          </div>
        </div>

        {/* Suspend / restore, with a reason the clinic can read. */}
        <div className="card">
          <div className="card-head"><h2>{suspended ? t.platform.restore : t.platform.suspend}</h2></div>
          <div className="card-body stack">
            <div className="notice warn"><span>{t.platform.suspendNote}</span></div>
            <Field label={t.platform.reason} hint={t.platform.reasonHint} required>
              <input value={status.reason} onChange={(e) => setStatus({ reason: e.target.value })} />
            </Field>
            <div className="row" style={{ justifyContent: 'flex-end' }}>
              <button
                type="button"
                className={suspended ? 'btn' : 'btn btn-danger'}
                disabled={busy !== null || status.reason.trim().length < 3}
                onClick={() => void act('status', async () => { await api.setClinicStatus(clinicId, { status: suspended ? 'active' : 'suspended', reason: status.reason }); setStatus({ reason: '' }); })}
              >
                {busy === 'status' ? <Spinner /> : suspended ? t.platform.restore : t.platform.suspend}
              </button>
            </div>
          </div>
        </div>

        {/* Export, then remove. The order is enforced by the server. */}
        <div className="card">
          <div className="card-head"><h2>{t.platform.exportClinic}</h2></div>
          <div className="card-body stack">
            <div className="notice"><span>{t.platform.exportHint}</span></div>
            <div className="row" style={{ justifyContent: 'flex-end' }}>
              <button
                type="button"
                className="btn"
                disabled={busy !== null}
                onClick={() =>
                  void act('export', async () => {
                    const { blob, filename } = await api.exportClinic(clinicId);
                    const url = URL.createObjectURL(blob);
                    const a = document.createElement('a');
                    a.href = url;
                    a.download = filename;
                    a.click();
                    URL.revokeObjectURL(url);
                  })
                }
              >
                {busy === 'export' ? <Spinner /> : t.platform.exportClinic}
              </button>
            </div>
          </div>
        </div>

        <div className="card">
          <div className="card-head"><h2>{t.platform.deleteClinic}</h2></div>
          <div className="card-body stack">
            <div className="notice danger"><span>{t.platform.deleteHint}</span></div>
            <div className="grid-2">
              <Field label={t.platform.typeAddress} required>
                <input value={confirm.slug} onChange={(e) => setConfirm({ ...confirm, slug: e.target.value })} className="ltr-value" placeholder={c.slug} />
              </Field>
              <Field label={t.platform.reason} required>
                <input value={confirm.reason} onChange={(e) => setConfirm({ ...confirm, reason: e.target.value })} />
              </Field>
            </div>
            <div className="row" style={{ justifyContent: 'flex-end' }}>
              <button
                type="button"
                className="btn btn-danger"
                disabled={busy !== null || confirm.slug.trim() !== c.slug || confirm.reason.trim().length < 3}
                onClick={() =>
                  void act('purge', async () => {
                    await api.purgeClinic(clinicId, confirm.slug.trim(), confirm.reason.trim());
                    setNotice(t.platform.removed);
                    onChanged();
                    onClose();
                  })
                }
              >
                {busy === 'purge' ? <Spinner /> : t.platform.deleteForever}
              </button>
            </div>
          </div>
        </div>
      </div>
    </Modal>
  );
}

/** Bring a clinic back from its export file. */
function ImportClinic({ onClose, onDone }: { onClose: () => void; onDone: () => void }) {
  const { t } = useI18n();
  const [file, setFile] = useState<File | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<string | null>(null);

  return (
    <Modal
      title={t.platform.importClinic}
      onClose={result ? onDone : onClose}
      footer={
        result ? (
          <button type="button" className="btn" onClick={onDone}>{t.common.close}</button>
        ) : (
          <>
            <button type="button" className="btn btn-ghost" onClick={onClose} disabled={busy}>{t.patients.cancel}</button>
            <button
              type="button"
              className="btn"
              disabled={busy || !file}
              onClick={async () => {
                if (!file) return;
                setBusy(true);
                setError(null);
                try {
                  const archive = JSON.parse(await file.text()) as unknown;
                  const r = await api.importClinic(archive);
                  const rows = Object.values(r.inserted).reduce((sum, n) => sum + n, 0);
                  setResult(t.platform.imported.replace('{rows}', String(rows)).replace('{files}', String(r.files)));
                } catch (e) {
                  setError(e instanceof SyntaxError ? t.common.error : describe(e, t));
                } finally {
                  setBusy(false);
                }
              }}
            >
              {busy ? <Spinner /> : t.platform.importClinic}
            </button>
          </>
        )
      }
    >
      <div className="stack">
        {error ? <div className="notice danger" role="alert">{error}</div> : null}
        {result ? (
          <div className="notice" role="status"><span>{result}</span></div>
        ) : (
          <>
            <div className="notice"><span>{t.platform.importHint}</span></div>
            <Field label={t.platform.chooseFile} required>
              <input type="file" accept=".json,application/json" onChange={(e) => setFile(e.target.files?.[0] ?? null)} />
            </Field>
          </>
        )}
      </div>
    </Modal>
  );
}

function NewClinic({ plans, onClose, onDone }: { plans: string[]; onClose: () => void; onDone: () => void }) {
  const { t } = useI18n();
  const [issued, setIssued] = useState<{ code: string; email: string; emailed: boolean } | null>(null);
  const [form, setForm] = useState({
    nameEn: '',
    nameAr: '',
    slug: '',
    ownerName: '',
    ownerEmail: '',
    timezone: Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC',
    currency: 'USD',
    locale: 'ar' as 'en' | 'ar',
    plan: plans[0] ?? 'solo',
    specialties: ['dental'] as string[],
    months: '12',
    startsOn: '',
    endsOn: '',
  });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const set = (k: keyof typeof form) => (e: { target: { value: string } }) =>
    setForm((f) => ({ ...f, [k]: e.target.value }));

  return (
    <Modal
      title={t.platform.newClinic}
      onClose={issued ? onDone : onClose}
      footer={
        issued ? (
          <button type="button" className="btn" onClick={onDone}>
            {t.common.close}
          </button>
        ) : (
          <>
            <button type="button" className="btn btn-ghost" onClick={onClose} disabled={busy}>
              {t.patients.cancel}
            </button>
            <button
              type="button"
              className="btn"
              disabled={busy || !form.nameEn || !form.slug || !form.ownerEmail || !form.ownerName}
              onClick={async () => {
                setBusy(true);
                setError(null);
                try {
                  const r = await api.createPlatformClinic({
                    nameEn: form.nameEn,
                    nameAr: form.nameAr || form.nameEn,
                    slug: form.slug,
                    ownerName: form.ownerName,
                    ownerEmail: form.ownerEmail,
                    timezone: form.timezone,
                    currency: form.currency,
                    locale: form.locale,
                    plan: form.plan,
                    specialties: form.specialties,
                    months: Number(form.months) || 12,
                    ...(form.startsOn ? { startsOn: form.startsOn } : {}),
                    ...(form.endsOn ? { endsOn: form.endsOn } : {}),
                  });
                  setIssued({ code: r.activationCode.code, email: r.activationCode.email, emailed: r.activationCode.emailed });
                } catch (e) {
                  setError(describe(e, t));
                } finally {
                  setBusy(false);
                }
              }}
            >
              {busy ? <Spinner /> : t.chart.save}
            </button>
          </>
        )
      }
    >
      <div className="stack">
        {error ? (
          <div className="notice danger" role="alert">
            {error}
          </div>
        ) : null}

        {issued ? (
          <>
            <CodeReveal code={issued.code} email={issued.email} note={t.platform.clinicCreated} />
            <div className={issued.emailed ? 'notice ok' : 'notice warn'} role="status">
              <span>{(issued.emailed ? t.platform.codeEmailed : t.platform.codeNotEmailed).replace('{email}', issued.email)}</span>
            </div>
          </>
        ) : (
          <div className="notice">
            <span>{t.platform.oneAccountNote}</span>
          </div>
        )}

        <div className="grid-2" style={issued ? { display: 'none' } : undefined}>
          <Field label={t.platform.clinicName} required>
            <input value={form.nameEn} onChange={set('nameEn')} autoFocus />
          </Field>
          <Field label={`${t.platform.clinicName} (العربية)`}>
            <input value={form.nameAr} onChange={set('nameAr')} dir="rtl" />
          </Field>
          <Field label={t.platform.address} hint={t.platform.addressHint} required>
            <input
              value={form.slug}
              onChange={(e) => setForm((f) => ({ ...f, slug: e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, '-') }))}
              className="ltr-value"
            />
          </Field>
          <Field label={t.settings.timezone}>
            <input value={form.timezone} onChange={set('timezone')} className="ltr-value" />
          </Field>
          <Field label={t.platform.ownerName} required>
            <input value={form.ownerName} onChange={set('ownerName')} />
          </Field>
          <Field label={t.platform.ownerEmail} required>
            <input type="email" value={form.ownerEmail} onChange={set('ownerEmail')} className="ltr-value" />
          </Field>
          <Field label={t.platform.plan} required>
            <select value={form.plan} onChange={set('plan')}>
              {plans.map((p) => (
                <option key={p} value={p}>
                  {p}
                </option>
              ))}
            </select>
          </Field>
          <Field label={t.settings.currency}>
            <input
              value={form.currency}
              onChange={(e) => setForm((f) => ({ ...f, currency: e.target.value.toUpperCase().slice(0, 3) }))}
              className="ltr-value"
            />
          </Field>
          <Field label={t.platform.specialties} hint={t.platform.specialtiesHint}>
            <div className="row" style={{ flexWrap: 'wrap', gap: 12 }}>
              {(['dental', 'dermatology', 'aesthetics'] as const).map((sp) => (
                <label key={sp} className="row" style={{ gap: 6 }}>
                  <input
                    type="checkbox"
                    checked={form.specialties.includes(sp)}
                    onChange={(e) =>
                      setForm((f) => {
                        const next = e.target.checked ? [...f.specialties, sp] : f.specialties.filter((x) => x !== sp);
                        return { ...f, specialties: next.length ? next : f.specialties };
                      })
                    }
                  />
                  <span>{(t.settings.specialty as Record<string, string>)[sp]}</span>
                </label>
              ))}
            </div>
          </Field>
          <Field label={t.platform.emailLanguage} hint={t.platform.emailLanguageHint}>
            <select value={form.locale} onChange={(e) => setForm((f) => ({ ...f, locale: e.target.value === 'en' ? 'en' : 'ar' }))}>
              <option value="ar">العربية</option>
              <option value="en">English</option>
            </select>
          </Field>
          <Field label={t.platform.startsOn}>
            <input type="date" value={form.startsOn} onChange={set('startsOn')} className="ltr-value" />
          </Field>
          <Field label={t.platform.endsOn} hint={t.platform.datesHint}>
            <input type="date" value={form.endsOn} onChange={set('endsOn')} className="ltr-value" />
          </Field>
          <Field label={t.platform.months}>
            <input value={form.months} onChange={set('months')} inputMode="numeric" className="ltr-value" disabled={!!form.endsOn} />
          </Field>
        </div>
      </div>
    </Modal>
  );
}

function Support() {
  const { t, formatDate, formatTime, locale } = useI18n();
  const [rows, setRows] = useState<SupportGrant[] | null>(null);
  const [clinics, setClinics] = useState<PlatformOverview['clinics']>([]);
  const [error, setError] = useState<string | null>(null);
  const [asking, setAsking] = useState(false);
  const [nonce, setNonce] = useState(0);

  useEffect(() => {
    setRows(null);
    void Promise.all([api.supportAccess(), api.platformOverview()])
      .then(([g, o]) => {
        setRows(g.grants);
        setClinics(o.clinics);
      })
      .catch((e) => setError(describe(e, t)));
  }, [nonce, t]);

  if (error) return <ErrorState message={error} onRetry={() => setNonce((n) => n + 1)} />;

  return (
    <>
      <div className="notice" style={{ marginBottom: 18 }}>
        <span>{t.platform.supportNote}</span>
      </div>

      <div className="toolbar">
        <div className="toolbar-spacer" />
        <button type="button" className="btn" onClick={() => setAsking(true)}>
          <IconPlus size={16} />
          {t.platform.grantSupport}
        </button>
      </div>

      <div className="card">
        {rows === null ? (
          <Skeleton rows={3} />
        ) : rows.length === 0 ? (
          <EmptyState title={t.platform.noGrants} />
        ) : (
          <div className="table-scroll">
            <table>
              <thead>
                <tr>
                  <th>{t.platform.clinicName}</th>
                  <th>{t.platform.scope}</th>
                  <th>{t.platform.reason}</th>
                  <th>{t.platform.expires}</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {rows.map((g) => (
                  <tr key={g.id}>
                    <td>
                      <strong>{g.clinicName}</strong>
                      <span className="sub ltr-value">{g.operatorEmail}</span>
                    </td>
                    <td>
                      <span className="badge">
                        {(t.platform.scopes as Record<string, string>)[g.scope] ?? g.scope}
                      </span>
                    </td>
                    <td>{g.reason}</td>
                    <td>
                      {formatDate(g.expiresAt)} {formatTime(g.expiresAt)}
                      <span className="sub">
                        <span className={`badge ${g.active ? 'ok' : ''}`}>
                          {g.active ? t.platform.active : t.platform.ended}
                        </span>
                      </span>
                    </td>
                    <td style={{ textAlign: 'end' }}>
                      {g.active ? (
                        <button
                          type="button"
                          className="btn-link"
                          onClick={async () => {
                            try {
                              await api.endSupportAccess(g.id);
                              setNonce((n) => n + 1);
                            } catch (e) {
                              setError(describe(e, t));
                            }
                          }}
                        >
                          {t.platform.endAccess}
                        </button>
                      ) : null}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {asking ? (
        <AskSupport
          clinics={clinics}
          locale={locale}
          onClose={() => setAsking(false)}
          onDone={() => {
            setAsking(false);
            setNonce((n) => n + 1);
          }}
        />
      ) : null}
    </>
  );
}

function AskSupport({
  clinics,
  locale,
  onClose,
  onDone,
}: {
  clinics: PlatformOverview['clinics'];
  locale: string;
  onClose: () => void;
  onDone: () => void;
}) {
  const { t } = useI18n();
  const [form, setForm] = useState({
    clinicId: clinics[0]?.id ?? '',
    reason: '',
    scope: 'configuration',
    hours: '2',
  });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  return (
    <Modal
      title={t.platform.grantSupport}
      onClose={onClose}
      footer={
        <>
          <button type="button" className="btn btn-ghost" onClick={onClose} disabled={busy}>
            {t.patients.cancel}
          </button>
          <button
            type="button"
            className="btn"
            disabled={busy || !form.clinicId || form.reason.trim().length < 10}
            onClick={async () => {
              setBusy(true);
              setError(null);
              try {
                await api.grantSupportAccess({ ...form, hours: Number(form.hours) });
                onDone();
              } catch (e) {
                setError(describe(e, t));
              } finally {
                setBusy(false);
              }
            }}
          >
            {busy ? <Spinner /> : t.platform.grantSupport}
          </button>
        </>
      }
    >
      <div className="stack">
        {error ? (
          <div className="notice danger" role="alert">
            {error}
          </div>
        ) : null}
        <div className="notice warn">
          <span>{t.platform.supportNote}</span>
        </div>
        <Field label={t.platform.clinicName} required>
          <select value={form.clinicId} onChange={(e) => setForm({ ...form, clinicId: e.target.value })}>
            {clinics.map((c) => (
              <option key={c.id} value={c.id}>
                {locale === 'ar' ? c.nameAr : c.nameEn}
              </option>
            ))}
          </select>
        </Field>
        <div className="grid-2">
          <Field label={t.platform.scope} required>
            <select value={form.scope} onChange={(e) => setForm({ ...form, scope: e.target.value })}>
              {(['configuration', 'billing', 'clinical'] as const).map((s) => (
                <option key={s} value={s}>
                  {(t.platform.scopes as Record<string, string>)[s]}
                </option>
              ))}
            </select>
          </Field>
          <Field label={t.platform.hours} required>
            <input
              value={form.hours}
              onChange={(e) => setForm({ ...form, hours: e.target.value })}
              inputMode="numeric"
              className="ltr-value"
            />
          </Field>
        </div>
        <Field label={t.platform.reason} hint={t.platform.reasonHint} required>
          <textarea value={form.reason} onChange={(e) => setForm({ ...form, reason: e.target.value })} rows={3} />
        </Field>
      </div>
    </Modal>
  );
}

function Operators() {
  const { t } = useI18n();
  const [rows, setRows] = useState<Awaited<ReturnType<typeof api.platformOperators>>['operators'] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [email, setEmail] = useState('');
  const [busy, setBusy] = useState(false);
  const [nonce, setNonce] = useState(0);

  useEffect(() => {
    setRows(null);
    api
      .platformOperators()
      .then((r) => setRows(r.operators))
      .catch((e) => setError(describe(e, t)));
  }, [nonce, t]);

  if (error) return <ErrorState message={error} onRetry={() => setNonce((n) => n + 1)} />;

  async function set(target: string, isOperator: boolean) {
    setBusy(true);
    setError(null);
    try {
      await api.setPlatformOperator({ email: target, isOperator });
      setEmail('');
      setNonce((n) => n + 1);
    } catch (e) {
      setError(describe(e, t));
    } finally {
      setBusy(false);
    }
  }

  return (
    <>
      <div className="notice" style={{ marginBottom: 18 }}>
        <span>{t.platform.operatorNote}</span>
      </div>

      <div className="card">
        <div className="card-head">
          <h2>{t.platform.operators}</h2>
        </div>
        {rows === null ? (
          <Skeleton rows={3} />
        ) : (
          <div className="table-scroll">
            <table>
              <tbody>
                {rows.map((o) => (
                  <tr key={o.id}>
                    <td>
                      <strong>{o.name}</strong>
                      <span className="sub ltr-value">{o.email}</span>
                    </td>
                    <td>
                      {o.isYou ? <span className="badge brand">{t.platform.you}</span> : null}
                      {!o.hasPassword ? <span className="badge warn">{t.platform.codes}</span> : null}
                    </td>
                    <td style={{ textAlign: 'end' }}>
                      {/* Never yourself: removing your own access by mistake
                          locks everyone out, and granting it to yourself is the
                          one change nobody else would have reviewed. */}
                      {o.isYou ? null : (
                        <button type="button" className="btn-link" disabled={busy} onClick={() => void set(o.email, false)}>
                          {t.platform.revoke}
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        <div className="card-body" style={{ borderTop: '1px solid var(--line-2)' }}>
          <div className="row" style={{ flexWrap: 'wrap' }}>
            <div className="search-box" style={{ maxWidth: 320 }}>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder={t.platform.ownerEmail}
                className="ltr-value"
                style={{ paddingInlineStart: 12 }}
              />
            </div>
            <button type="button" className="btn" disabled={busy || !email} onClick={() => void set(email, true)}>
              {t.platform.makeOperator}
            </button>
          </div>
        </div>
      </div>
    </>
  );
}

function Stat({ label, value, tone }: { label: string; value: string; tone: string }) {
  return (
    <div className="card stat">
      <div className="stat-top">
        <span>{label}</span>
        <span className={`stat-icon ${tone}`} aria-hidden="true">
          ●
        </span>
      </div>
      <div className="stat-value ltr-value">{value}</div>
    </div>
  );
}

